/**
 * `MapView` — the renderer's single entry point.
 *
 * It owns the canvas, the precomputed scene, the viewport, the density layer
 * and the input controllers, and it is the only thing in this module with
 * mutable state.
 *
 * ## How it stays smooth at 2000 nodes
 *
 * * **Nothing is recomputed per frame.**  Label boxes, label thresholds, circle
 *   diameters, the mean pairwise distance and the density grid all live in the
 *   `Scene` and are rebuilt only when the data or the graphics scale changes.
 * * **Frames are scheduled, not forced.**  Every mutation marks the view dirty
 *   and asks for one `requestAnimationFrame`; ten changes in one tick produce
 *   one repaint, and an idle map produces none at all.
 * * **Panning blits.**  A drag does not move the data, only the window onto it,
 *   so the frame at the start of the drag is rendered once into an oversized
 *   offscreen buffer and then blitted at an offset.  The buffer carries a
 *   margin, and is re-rendered when the drag runs past it.  Screen-space
 *   chrome — the colour bar, the focus ring — is drawn fresh on top so it does
 *   not slide with the map.
 */

import type {
  HoverPayload,
  MapDocument,
  MapEventName,
  MapItem,
  MapViewOptions,
  SelectPayload,
  Theme,
  ViewChangePayload,
  ViewMode,
} from './types';
import { Viewport } from './geometry/viewport';
import { buildScene, resolveSceneOptions, type Scene, type SceneOptions } from './model/scene';
import { buildOverlay, type OverlayModel } from './model/overlay';
import { hitTest } from './model/hitTest';
import { css } from './color/lab';
import { schemeByName } from './color/schemes';
import { resolveTheme, watchColorScheme, watchReducedMotion, prefersReducedMotion } from './color/theme';
import { DensityLayer, defaultDensityConfig, type DensityConfig } from './layers/density';
import { CircleSprites } from './layers/sprites';
import { drawChrome, drawFrame, type RenderState } from './layers/network';
import { PointerController } from './interaction/pointer';
import { KeyboardController } from './interaction/keyboard';
import { MAX_N_LINES } from './model/constants';

/** Extra pixels rendered around the canvas into the pan buffer. */
const PAN_MARGIN = 192;

type Handler = (payload: unknown) => void;

const EMPTY_DOCUMENT: MapDocument = { items: [], links: [] };

export class MapView {
  private readonly canvas: HTMLCanvasElement;

  private readonly ctx: CanvasRenderingContext2D;

  private options: MapViewOptions;

  private sceneOptions: SceneOptions;

  private densityConfig: DensityConfig;

  private readonly viewport = new Viewport();

  private readonly sprites = new CircleSprites();

  private readonly density: DensityLayer;

  private readonly pointer: PointerController;

  private readonly keyboard: KeyboardController;

  private readonly listeners = new Map<MapEventName, Set<Handler>>();

  private scene: Scene;

  private doc: MapDocument = EMPTY_DOCUMENT;

  private mode: ViewMode = 'network';

  private scoreField: string | null = null;

  private overlay: OverlayModel | null = null;

  private theme: Theme;

  private hovered = -1;

  private selected = -1;

  private cursor = -1;

  private canvasFocused = false;

  private minLinkStrength: number;

  private maxLines: number;

  private frameHandle = 0;

  private dirty = false;

  private destroyed = false;

  private fitted = false;

  /**
   * True while the view still shows exactly what `fit()` chose.
   *
   * A canvas frequently gets its real size only after the first layout pass —
   * a flex or grid parent that has not resolved yet reports a few dozen
   * pixels — so an initial fit can be computed against a size that never
   * existed on screen. While this flag holds, a resize refits; the first pan
   * or zoom clears it, and from then on a resize reveals more of the map
   * instead of reframing it under the user.
   */
  private viewFollowsFit = false;

  private panBuffer: HTMLCanvasElement | null = null;

  private panActive = false;

  private panDx = 0;

  private panDy = 0;

  private readonly unwatchers: Array<() => void> = [];

  private resizeObserver: ResizeObserver | null = null;

  private reducedMotion: boolean;

  private lastVisibleLabels = 0;

  private animation: { handle: number; cancel: () => void } | null = null;

  constructor(canvas: HTMLCanvasElement, options: MapViewOptions = {}) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('MapView needs a 2D canvas context');
    this.canvas = canvas;
    this.ctx = ctx;
    this.options = { ...options };
    this.sceneOptions = resolveSceneOptions(this.options);
    this.theme = resolveTheme(this.options.theme ?? 'auto');
    this.reducedMotion = this.options.reducedMotion ?? prefersReducedMotion();
    this.minLinkStrength = this.options.minLinkStrength ?? 0;
    this.maxLines = this.options.maxLines ?? MAX_N_LINES;

    this.densityConfig = {
      ...defaultDensityConfig(schemeByName(this.options.densityScheme)),
      ...(this.options.densityGridSize !== undefined
        ? { gridSize: this.options.densityGridSize }
        : {}),
      ...(this.options.colorTransformation !== undefined
        ? { colorTransformation: this.options.colorTransformation }
        : {}),
      forceCpu: this.options.forceCpuDensity ?? false,
    };
    this.density = new DensityLayer(this.densityConfig);

    this.scene = buildScene(EMPTY_DOCUMENT, ctx, this.sceneOptions);

    this.pointer = new PointerController(canvas, {
      onPanStart: () => {
        this.cancelAnimation();
        this.beginPan();
      },
      onPan: (dx, dy) => {
        this.viewFollowsFit = false;
        this.pan(dx, dy);
      },
      onPanEnd: () => this.endPan(),
      onZoom: (factor, anchor) => {
        this.cancelAnimation();
        this.viewFollowsFit = false;
        this.viewport.zoomBy(factor, anchor);
        this.invalidate();
      },
      onHover: (x, y) => this.updateHover(x, y),
      onHoverEnd: () => this.updateHover(-1, -1, true),
      onClick: (x, y) => this.handleClick(x, y),
    });

    this.keyboard = new KeyboardController(canvas, {
      onPan: (dx, dy) => {
        this.viewFollowsFit = false;
        this.viewport.panByPixels(dx, dy);
        this.invalidate();
      },
      onZoom: (factor) => {
        this.viewFollowsFit = false;
        this.viewport.zoomBy(factor);
        this.invalidate();
      },
      onFit: () => this.fit(),
      onCursorStep: (delta) => this.stepCursor(delta),
      onCursorEnd: (which) => this.jumpCursor(which),
      onActivate: () => this.activateCursor(),
      onEscape: () => {
        this.selected = -1;
        this.cursor = -1;
        this.emit('select', { item: null, index: -1, at: null } satisfies SelectPayload);
        this.invalidate();
      },
    });

    canvas.addEventListener('focus', this.onFocus);
    canvas.addEventListener('blur', this.onBlur);

    if (this.options.theme === undefined || this.options.theme === 'auto') {
      this.unwatchers.push(
        watchColorScheme(() => {
          this.theme = resolveTheme(this.options.theme ?? 'auto');
          this.invalidate();
        }),
      );
    }
    if (this.options.reducedMotion === undefined) {
      this.unwatchers.push(
        watchReducedMotion(() => {
          this.reducedMotion = prefersReducedMotion();
        }),
      );
    }

    this.observeSize();
    this.syncCanvasSize();
  }

  // ---------------------------------------------------------------- contract

  setData(doc: MapDocument): void {
    if (this.destroyed) return;
    this.doc = doc;
    this.scene = buildScene(doc, this.ctx, this.sceneOptions);
    this.sprites.clear();
    this.hovered = -1;
    this.selected = -1;
    this.cursor = -1;
    this.density.setScene(this.scene);
    this.rebuildOverlay();
    if ((this.options.autoFit ?? true) || !this.fitted) this.fit();
    else this.invalidate();
  }

  setMode(mode: ViewMode): void {
    if (this.destroyed || mode === this.mode) return;
    this.mode = mode;
    // Entering the overlay with no field chosen picks the first one the data
    // offers, so the view is never a flat grey plate for want of one call.
    if (mode === 'overlay' && this.scoreField === null && this.scene.scoreFields.length > 0) {
      this.setScoreField(this.scene.scoreFields[0] as string);
      return;
    }
    this.invalidate();
  }

  setScoreField(field: string | null): void {
    if (this.destroyed) return;
    this.scoreField = field;
    this.rebuildOverlay();
    this.invalidate();
  }

  setLinkVisibility(minStrength: number, maxLines: number): void {
    if (this.destroyed) return;
    this.minLinkStrength = Number.isFinite(minStrength) ? minStrength : 0;
    this.maxLines = Number.isFinite(maxLines) ? Math.max(0, Math.floor(maxLines)) : MAX_N_LINES;
    this.invalidate();
  }

  fit(): void {
    if (this.destroyed) return;
    this.syncCanvasSize();
    this.viewport.fitTo(this.scene.bounds, this.options.padding ?? 0.05);
    this.fitted = true;
    this.viewFollowsFit = true;
    this.invalidate();
  }

  /**
   * Multiply the zoom.  `centre` is a canvas-relative point in CSS pixels that
   * should stay under the same pixel; omit it to zoom about the canvas centre.
   */
  zoomBy(factor: number, centre?: [number, number]): void {
    if (this.destroyed) return;
    this.viewFollowsFit = false;
    this.viewport.zoomBy(factor, centre);
    this.invalidate();
  }

  on(event: MapEventName, handler: (payload: unknown) => void): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set<Handler>();
      this.listeners.set(event, set);
    }
    set.add(handler);
    return () => {
      set?.delete(handler);
    };
  }

  /**
   * Render at `scale` times the on-screen size and encode a PNG.
   *
   * The export re-renders rather than copying the canvas, so it is not limited
   * to the device pixel ratio and it can leave out the things that are about
   * interaction rather than about the map: no tooltip, no focus ring, no
   * hover highlight.  The colour bar stays, because a legend is part of the
   * figure.
   */
  async exportPNG(scale = 2): Promise<Blob> {
    if (this.destroyed) throw new Error('MapView has been destroyed');
    const width = this.viewport.width;
    const height = this.viewport.height;
    const factor = Math.max(0.1, Math.min(8, scale));
    const target = document.createElement('canvas');
    target.width = Math.max(1, Math.round(width * factor));
    target.height = Math.max(1, Math.round(height * factor));
    const ctx = target.getContext('2d');
    if (!ctx) throw new Error('could not create an export canvas');
    ctx.scale(factor, factor);

    const state = this.renderState({ hovered: -1, canvasFocused: false, focused: -1, tooltip: false });
    this.paint(ctx, state, width, height, true);

    return await new Promise<Blob>((resolve, reject) => {
      target.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('canvas.toBlob produced no data'));
      }, 'image/png');
    });
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    if (this.frameHandle) cancelAnimationFrame(this.frameHandle);
    this.frameHandle = 0;
    this.cancelAnimation();
    this.pointer.destroy();
    this.keyboard.destroy();
    this.canvas.removeEventListener('focus', this.onFocus);
    this.canvas.removeEventListener('blur', this.onBlur);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    for (const off of this.unwatchers) off();
    this.unwatchers.length = 0;
    this.listeners.clear();
    this.density.destroy();
    this.sprites.clear();
    this.panBuffer = null;
  }

  // ------------------------------------------------------------------ extras

  /** Score fields the current data offers, in first-seen order. */
  scoreFields(): string[] {
    return [...this.scene.scoreFields];
  }

  /** `'webgl'` when the density field is computed on the GPU, `'cpu'` otherwise. */
  densityBackend(): 'webgl' | 'cpu' {
    return this.density.backend;
  }

  /** Select an item by id, or clear the selection with `null`. */
  select(id: string | null): void {
    if (this.destroyed) return;
    if (id === null) {
      this.selected = -1;
    } else {
      const index = this.doc.items.findIndex((item) => item.id === id);
      this.selected = index;
      if (index >= 0) this.cursor = index;
    }
    this.emitSelect(this.selected, null);
    this.invalidate();
  }

  /** Apply a partial option change; rebuilds only what the change affects. */
  setOptions(patch: MapViewOptions): void {
    if (this.destroyed) return;
    this.options = { ...this.options, ...patch };
    const nextScene = resolveSceneOptions(this.options);
    const sceneChanged =
      nextScene.scale !== this.sceneOptions.scale ||
      nextScene.labelSizeVariation !== this.sceneOptions.labelSizeVariation ||
      nextScene.maxLabelLength !== this.sceneOptions.maxLabelLength ||
      nextScene.fontFamily !== this.sceneOptions.fontFamily ||
      nextScene.kernelWidth !== this.sceneOptions.kernelWidth ||
      nextScene.minRelativeClusterWeight !== this.sceneOptions.minRelativeClusterWeight ||
      nextScene.clusterBase !== this.sceneOptions.clusterBase ||
      nextScene.clusterPalette !== this.sceneOptions.clusterPalette;
    this.sceneOptions = nextScene;
    this.theme = resolveTheme(this.options.theme ?? 'auto');
    this.reducedMotion = this.options.reducedMotion ?? prefersReducedMotion();

    this.densityConfig = {
      ...this.densityConfig,
      scheme: schemeByName(this.options.densityScheme),
      gridSize: this.options.densityGridSize ?? this.densityConfig.gridSize,
      colorTransformation:
        this.options.colorTransformation ?? this.densityConfig.colorTransformation,
      forceCpu: this.options.forceCpuDensity ?? this.densityConfig.forceCpu,
    };

    if (sceneChanged) {
      this.scene = buildScene(this.doc, this.ctx, this.sceneOptions);
      this.sprites.clear();
      this.density.setConfig(this.densityConfig);
      this.density.setScene(this.scene);
    } else {
      this.density.setConfig(this.densityConfig);
    }
    this.rebuildOverlay();
    this.invalidate();
  }

  /** Re-read the canvas's CSS size. Called automatically by a `ResizeObserver`. */
  resize(): void {
    if (this.destroyed) return;
    this.syncCanvasSize();
    this.invalidate();
  }

  // ----------------------------------------------------------------- private

  private rebuildOverlay(): void {
    if (this.scoreField === null || this.scene.n === 0) {
      this.overlay = null;
      return;
    }
    this.overlay = buildOverlay(
      this.doc.items,
      this.scoreField,
      schemeByName(this.options.overlayScheme),
      this.theme.noValue,
    );
  }

  private renderState(override: {
    hovered: number;
    canvasFocused: boolean;
    focused: number;
    tooltip: boolean;
  }): RenderState {
    const density = this.isDensityMode();
    return {
      densityGround: density
        ? this.density.backgroundFor(this.mode as 'item-density' | 'cluster-density', this.theme)
        : null,
      mode: this.mode,
      theme: this.theme,
      overlay: this.overlay,
      overlayScheme: this.overlay ? schemeByName(this.options.overlayScheme) : null,
      hovered: override.hovered,
      selected: this.selected,
      focused: override.focused,
      minLinkStrength: this.minLinkStrength,
      maxLines: this.maxLines,
      showLegend: (this.options.showLegend ?? true) && this.mode === 'overlay',
      showTooltip: override.tooltip && (this.options.showTooltip ?? true),
      canvasFocused: override.canvasFocused,
      fontFamily: this.sceneOptions.fontFamily,
      scale: this.sceneOptions.scale,
    };
  }

  private isDensityMode(): boolean {
    return this.mode === 'item-density' || this.mode === 'cluster-density';
  }

  /** Paint background, density field and map content into `ctx`. */
  private paint(
    ctx: CanvasRenderingContext2D,
    state: RenderState,
    width: number,
    height: number,
    chrome: boolean,
  ): { visibleLabels: number } {
    const density = this.isDensityMode();
    const background = density
      ? this.density.backgroundFor(this.mode as 'item-density' | 'cluster-density', this.theme)
      : this.theme.background;
    ctx.fillStyle = css(background);
    ctx.fillRect(0, 0, width, height);
    if (density) {
      this.density.draw(
        ctx,
        this.mode as 'item-density' | 'cluster-density',
        this.theme,
        (x, y) => [this.viewport.toScreenX(x), this.viewport.toScreenY(y)],
      );
    }
    const result = drawFrame(
      ctx,
      this.scene,
      this.viewport,
      state,
      this.sprites,
      width,
      height,
      chrome,
    );
    return { visibleLabels: result.visibleLabels };
  }

  private invalidate(): void {
    if (this.destroyed) return;
    this.dirty = true;
    if (this.frameHandle) return;
    this.frameHandle = requestAnimationFrame(this.renderFrame);
  }

  private renderFrame = (): void => {
    this.frameHandle = 0;
    if (this.destroyed || !this.dirty) return;
    this.dirty = false;

    const width = this.viewport.width;
    const height = this.viewport.height;
    const ctx = this.ctx;
    const dpr = this.pixelRatio();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const state = this.renderState({
      hovered: this.hovered,
      canvasFocused: this.canvasFocused,
      focused: this.cursor,
      tooltip: true,
    });

    let visibleLabels: number;
    if (this.panActive && this.panBuffer) {
      // Blit the pre-rendered frame at the drag offset, then repaint the
      // screen-space chrome so it stays put.
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(
        this.panBuffer,
        -PAN_MARGIN + this.panDx,
        -PAN_MARGIN + this.panDy,
        this.panBuffer.width / dpr,
        this.panBuffer.height / dpr,
      );
      drawChrome(ctx, this.scene, this.viewport, state, width, height);
      visibleLabels = this.lastVisibleLabels;
    } else {
      visibleLabels = this.paint(ctx, state, width, height, true).visibleLabels;
      this.lastVisibleLabels = visibleLabels;
    }

    this.emit('viewchange', {
      scale: this.viewport.scale,
      centre: [this.viewport.centreX, this.viewport.centreY],
      bounds: this.viewport.visibleBounds(),
      visibleLabels,
    } satisfies ViewChangePayload);
  };

  private pixelRatio(): number {
    const dpr = this.options.pixelRatio ?? (typeof window !== 'undefined' ? window.devicePixelRatio : 1);
    return Number.isFinite(dpr) && dpr > 0 ? dpr : 1;
  }

  private syncCanvasSize(): void {
    const rect = this.canvas.getBoundingClientRect();
    // A canvas with no CSS size at all (a detached element, or one rendered
    // before layout) still has its attribute size; fall back to it so the
    // first frame is not 1x1.
    const cssWidth = rect.width > 0 ? rect.width : this.canvas.width || 1;
    const cssHeight = rect.height > 0 ? rect.height : this.canvas.height || 1;
    const dpr = this.pixelRatio();
    const pixelWidth = Math.max(1, Math.round(cssWidth * dpr));
    const pixelHeight = Math.max(1, Math.round(cssHeight * dpr));
    if (this.canvas.width !== pixelWidth || this.canvas.height !== pixelHeight) {
      this.canvas.width = pixelWidth;
      this.canvas.height = pixelHeight;
    }
    this.viewport.setSize(cssWidth, cssHeight);
  }

  private observeSize(): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver(() => {
      this.syncCanvasSize();
      // A view the user has already moved keeps its scale: a panel that gets
      // wider should reveal more of the map, not magnify what was there. An
      // untouched view refits, which is also what rescues a first fit computed
      // before the canvas had its real size.
      if (this.viewFollowsFit) {
        this.viewport.fitTo(this.scene.bounds, this.options.padding ?? 0.05);
      }
      this.invalidate();
    });
    this.resizeObserver.observe(this.canvas);
  }

  private beginPan(): void {
    if (this.scene.n === 0 && !this.isDensityMode()) return;
    const dpr = this.pixelRatio();
    const width = this.viewport.width + PAN_MARGIN * 2;
    const height = this.viewport.height + PAN_MARGIN * 2;
    const buffer = this.panBuffer ?? document.createElement('canvas');
    buffer.width = Math.max(1, Math.round(width * dpr));
    buffer.height = Math.max(1, Math.round(height * dpr));
    const ctx = buffer.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Render with the viewport temporarily enlarged around the same centre, so
    // the buffer's middle lines up with the canvas's middle.
    const realWidth = this.viewport.width;
    const realHeight = this.viewport.height;
    this.viewport.setSize(width, height);
    const state = this.renderState({
      hovered: -1,
      canvasFocused: false,
      focused: -1,
      tooltip: false,
    });
    this.paint(ctx, state, width, height, false);
    this.viewport.setSize(realWidth, realHeight);

    this.panBuffer = buffer;
    this.panActive = true;
    this.panDx = 0;
    this.panDy = 0;
    this.hovered = -1;
  }

  private pan(dx: number, dy: number): void {
    this.viewport.panByPixels(dx, dy);
    this.panDx += dx;
    this.panDy += dy;
    // Past the margin the buffer has no content left to show on that edge, so
    // re-render it around the new centre.
    if (Math.abs(this.panDx) > PAN_MARGIN || Math.abs(this.panDy) > PAN_MARGIN) {
      this.beginPan();
    }
    this.invalidate();
  }

  private endPan(): void {
    this.panActive = false;
    this.invalidate();
  }

  private updateHover(x: number, y: number, leaving = false): void {
    if (this.panActive) return;
    const index = leaving ? -1 : hitTest(this.scene, this.viewport, x, y);
    if (index === this.hovered) return;
    this.hovered = index;
    const item = index >= 0 ? (this.doc.items[index] as MapItem) : null;
    this.canvas.style.cursor = item ? (item.url ? 'pointer' : 'default') : 'default';
    this.emit('hover', {
      item,
      index,
      at: leaving ? null : [x, y],
    } satisfies HoverPayload);
    this.invalidate();
  }

  private handleClick(x: number, y: number): void {
    const index = hitTest(this.scene, this.viewport, x, y);
    this.selected = index;
    this.cursor = index;
    this.emitSelect(index, [x, y]);
    this.invalidate();
  }

  private emitSelect(index: number, at: readonly [number, number] | null): void {
    this.emit('select', {
      item: index >= 0 ? (this.doc.items[index] as MapItem) : null,
      index,
      at,
    } satisfies SelectPayload);
  }

  private stepCursor(delta: number): void {
    const n = this.scene.n;
    if (n === 0) return;
    const order = this.scene.order;
    let rank = 0;
    if (this.cursor >= 0) {
      for (let r = 0; r < n; r += 1) {
        if ((order[r] as number) === this.cursor) {
          rank = r + delta;
          break;
        }
      }
    } else {
      rank = delta > 0 ? 0 : n - 1;
    }
    this.setCursorRank(Math.min(n - 1, Math.max(0, rank)));
  }

  private jumpCursor(which: -1 | 1): void {
    if (this.scene.n === 0) return;
    this.setCursorRank(which === -1 ? 0 : this.scene.n - 1);
  }

  private setCursorRank(rank: number): void {
    const index = this.scene.order[rank];
    if (index === undefined) return;
    this.cursor = index;
    // Bring the item into view without changing the zoom, so a keyboard walk
    // through the map keeps its sense of scale.
    this.viewFollowsFit = false;
    this.glideTo(this.scene.x[index] as number, this.scene.y[index] as number);
    this.emitSelect(index, null);
    this.selected = index;
    this.invalidate();
  }

  /**
   * Recentre the view, easing there over ~220 ms.
   *
   * The easing is what tells a keyboard user that the map moved rather than
   * changed: a jump between two items ten screens apart is disorienting, a
   * glide is not.  Under `prefers-reduced-motion` it becomes a jump, because
   * for some readers that same glide is the problem.
   */
  private glideTo(x: number, y: number): void {
    this.cancelAnimation();
    if (this.reducedMotion) {
      this.viewport.centreX = x;
      this.viewport.centreY = y;
      this.invalidate();
      return;
    }
    const fromX = this.viewport.centreX;
    const fromY = this.viewport.centreY;
    const start = performance.now();
    const duration = 220;
    let cancelled = false;
    const step = (now: number): void => {
      if (cancelled || this.destroyed) return;
      const t = Math.min(1, (now - start) / duration);
      // Cubic ease-out: fast away from the old position, gentle into the new.
      const eased = 1 - Math.pow(1 - t, 3);
      this.viewport.centreX = fromX + (x - fromX) * eased;
      this.viewport.centreY = fromY + (y - fromY) * eased;
      this.invalidate();
      if (t < 1) {
        this.animation = { handle: requestAnimationFrame(step), cancel: () => {
          cancelled = true;
        } };
      } else {
        this.animation = null;
      }
    };
    this.animation = { handle: requestAnimationFrame(step), cancel: () => {
      cancelled = true;
    } };
  }

  private cancelAnimation(): void {
    if (!this.animation) return;
    cancelAnimationFrame(this.animation.handle);
    this.animation.cancel();
    this.animation = null;
  }

  private activateCursor(): void {
    if (this.cursor < 0) return;
    this.selected = this.cursor;
    this.emitSelect(this.cursor, null);
    this.invalidate();
  }

  private onFocus = (): void => {
    this.canvasFocused = true;
    this.invalidate();
  };

  private onBlur = (): void => {
    this.canvasFocused = false;
    this.invalidate();
  };

  private emit(event: MapEventName, payload: unknown): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const handler of set) {
      try {
        handler(payload);
      } catch (error) {
        // A throwing listener must not take the render loop down with it.
        console.error(`[journalmap/render] ${event} handler threw`, error);
      }
    }
  }
}
